import { useEffect, useState } from 'react';
import { BrowserRouter, Route, Routes, useParams } from 'react-router-dom';
import { Toaster } from 'sonner';
import { ThemeProvider } from '@mui/material/styles';
import Layout from './layout';
import Chat from './app/chat/Chat';
import ChatWelcome from './app/chat/ChatWelcome';
import Home from './app/Home';
import Unauthorized from './app/auth/Unauthorized';
import AdminLayout from './components/admin/AdminLayout';
import Users from './app/admin/Users';
import Content from './app/admin/Content';
import FAQ from './app/admin/FAQ';
import FeedbackManagement from './app/admin/FeedbackManagement';
import { AuthProvider } from './context/AuthProvider';
import { ChatProvider } from './context/ChatContext';
import { SocketProvider } from './context/SocketContext';
import { refreshIdToken } from './services/authService';
import PublicRoute from './guards/PublicRoute';
import ProtectedRoute from './guards/ProtectedRoute';
import AdminRoute from './guards/AdminRoute';
import theme from './theme/muiTheme';
import './App.css';

const ChatWithSocket = ({ children }) => {
  const { chatId } = useParams();
  const [token, setToken] = useState(() =>
    localStorage.getItem('firebase_id_token')
  );

  useEffect(() => {
    const refreshAndSetToken = async () => {
      try {
        const newToken = await refreshIdToken();
        if (newToken && newToken !== token) {
          setToken(newToken);
        }
      } catch (err) {
        console.error('Token refresh failed:', err);
      }
    };

    refreshAndSetToken();

    const interval = setInterval(() => {
      refreshAndSetToken();
    }, 25 * 60 * 1000);

    return () => clearInterval(interval);
  }, [token]);

  return (
    <SocketProvider token={token} chatId={chatId}>
      {children}
    </SocketProvider>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <BrowserRouter>
        <AuthProvider>
          <ChatProvider>
            <Toaster position="top-right" />
            <Routes>
              <Route
                path="/"
                element={
                  <PublicRoute>
                    <Home />
                  </PublicRoute>
                }
              />
              <Route path="/unauthorized" element={<Unauthorized />} />

              {/* Admin Routes */}
              <Route
                path="/admin/users"
                element={
                  <AdminRoute>
                    <AdminLayout>
                      <Users />
                    </AdminLayout>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/content"
                element={
                  <AdminRoute>
                    <AdminLayout>
                      <Content />
                    </AdminLayout>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/faq"
                element={
                  <AdminRoute>
                    <AdminLayout>
                      <FAQ />
                    </AdminLayout>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/feedback"
                element={
                  <AdminRoute>
                    <AdminLayout>
                      <FeedbackManagement />
                    </AdminLayout>
                  </AdminRoute>
                }
              />

              {/* Chat Routes */}
              <Route
                path="/chat"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ChatWithSocket>
                        <ChatWelcome />
                      </ChatWithSocket>
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/chat/:chatId"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ChatWithSocket>
                        <Chat />
                      </ChatWithSocket>
                    </Layout>
                  </ProtectedRoute>
                }
              />
            </Routes>
          </ChatProvider>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
